<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';

// Props
const props = defineProps({
  selectedAgentId: {
    type: Number,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits(['select']);

// State
const store = useStore();
const isLoading = ref(false);
const agents = ref([]);
const localSelectedId = ref(props.selectedAgentId);
const isDropdownOpen = ref(false);

// Get authentication headers
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) {
    //console.error('cw_d_session_info cookie não encontrado');
    return {};
  }
  try {
    const sessionInfo = JSON.parse(decodeURIComponent(cookie));
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    };
  } catch (e) {
    //console.error('Erro ao analisar cw_d_session_info:', e);
    return {};
  }
};

// Computed
const selectedAgent = computed(() => {
  return agents.value.find(agent => agent.id === localSelectedId.value);
});

const agentOptions = computed(() => {
  return agents.value.map(agent => ({
    value: agent.id,
    label: getAgentDisplayName(agent)
  }));
});

// Methods
const getAgentDisplayName = (agent) => {
  if (!agent) return '';
  
  // Tentar extrair o nome da empresa do company_data
  let companyName = '';
  if (agent.company_data) {
    try {
      const companyData = typeof agent.company_data === 'string'
        ? JSON.parse(agent.company_data)
        : agent.company_data;
      companyName = companyData.name || '';
    } catch (error) {
      //console.error('Erro ao processar company_data:', error);
    }
  }
  
  // Se tiver nome da empresa, incluir no display
  if (companyName) {
    return `${agent.name} - ${companyName}`;
  }
  
  return agent.name;
};

const fetchAgents = async () => {
  isLoading.value = true;
  
  try {
    const headers = getAuthHeaders();
    
    const response = await fetch('/api/v1/agents_ai', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...headers
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Erro ao buscar agentes: ${response.status}`);
    }

    const data = await response.json();
    
    if (data && data.agents && Array.isArray(data.agents)) {
      agents.value = data.agents;
    } else {
      //console.error('Formato de resposta inesperado:', data);
    }
  } catch (error) {
    //console.error('Erro ao carregar agentes:', error);
    // Tentar carregar do store como fallback
    try {
      await store.dispatch('agents/getAll');
      const storeAgents = store.getters['agents/getAgents'];
      if (storeAgents && Array.isArray(storeAgents)) {
        agents.value = storeAgents;
      }
    } catch (storeError) {
      //console.error('Erro ao carregar agentes do store:', storeError);
    }
  } finally {
    isLoading.value = false;
  }
};

const handleSelect = (agent) => {
  const agentId = agent ? agent.id : null;
  localSelectedId.value = agentId;
  isDropdownOpen.value = false;
  
  emit('select', agentId, agent);
};

const toggleDropdown = () => {
  if (!isLoading.value && !props.disabled) {
    isDropdownOpen.value = !isDropdownOpen.value;
  }
};

const handleClickOutside = (event) => {
  const dropdown = event.target.closest('.custom-dropdown');
  if (!dropdown) {
    isDropdownOpen.value = false;
  }
};

// Watchers
watch(() => props.selectedAgentId, (newValue) => {
  localSelectedId.value = newValue;
});

// Lifecycle
onMounted(() => {
  fetchAgents();
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="agent-selector">
    <div class="relative custom-dropdown">
      <button
        @click="toggleDropdown"
        type="button"
        :disabled="isLoading || disabled"
        class="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-n-slate-3 rounded-lg text-base text-n-slate-12 hover:border-n-brand focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 focus:outline-none transition-colors cursor-pointer text-left flex items-center justify-between disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span class="truncate min-w-0 flex-1" :class="{
          'text-n-slate-11': !selectedAgent,
          'text-n-slate-12': selectedAgent
        }">
          {{ selectedAgent ? getAgentDisplayName(selectedAgent) : 'Selecione um agente...' }}
        </span>
        <span 
          class="i-lucide-chevron-up ml-2 h-5 w-5 text-n-slate-11 transition-transform flex-shrink-0"
          :class="{ 'rotate-180': !isDropdownOpen }"
        ></span>
      </button>
      
      <!-- Lista de opções -->
      <div
        v-if="isDropdownOpen && !isLoading && !disabled"
        class="absolute top-full mt-1 left-0 w-full bg-white dark:bg-slate-900 border border-n-slate-3 rounded-lg shadow-lg z-50 overflow-hidden"
        @mousedown.prevent
      >
        <!-- Quando não há agentes -->
        <div v-if="agents.length === 0" class="px-4 py-3 text-base text-n-slate-11">
          Nenhum agente encontrado
        </div>
        
        <!-- Lista de agentes -->
        <div v-else class="max-h-60 overflow-y-auto">
          <ul class="py-1">
            <li
              v-for="agent in agents"
              :key="agent.id"
              class="px-4 py-3 cursor-pointer text-base transition-colors text-n-slate-12 min-w-0"
              :class="{
                'bg-n-brand/10 text-n-brand': localSelectedId === agent.id,
                'hover:bg-n-slate-1': localSelectedId !== agent.id
              }"
              @mousedown.prevent="handleSelect(agent)"
            >
              <div class="truncate w-full">
                {{ getAgentDisplayName(agent) }}
              </div>
            </li>
          </ul>
          
          <!-- Rodapé informativo -->
          <div class="px-4 py-2 text-xs text-n-slate-11 border-t border-n-slate-3 bg-n-slate-1">
            Selecione o agente para aplicar os ajustes
          </div>
        </div>
      </div>
    </div>
    
    <!-- Loading indicator -->
    <div v-if="isLoading" class="mt-2 text-sm text-n-slate-11">
      Carregando agentes...
    </div>
  </div>
</template>

